import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  AlertCircle,
  Copy,
  FileText,
  Loader2,
  Plus,
  Save,
  Trash2,
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../lib/api'
import { confirmDialog, toast } from '../../lib/dialogs'
import {
  DocumentTemplateCategoryLabel,
  type DocumentTemplateCategoryT,
  type DocumentTemplateDto,
  type DocumentTemplateListResponse,
  type TemplateVariableCatalogItem,
} from '@toga/shared'
import { useAuth } from '../../contexts/AuthContext'

/**
 * Punto de partida al crear una plantilla. Sustituye al escrito judicial que
 * arrastrábamos del CRM de despachos original: JR2 no presenta escritos, envía
 * propuestas y documentos de proyecto.
 */
const STARTER_TEMPLATE = `# {{oportunidad.title}}

**Para:** {{cliente.displayName}} ({{cliente.nationalId}})
**De:** {{compania.name}}
**Referencia:** {{oportunidad.code}}
**Fecha:** {{fecha.hoyLargo}}

## Contexto

(Describe la situación de partida del cliente y el problema a resolver.)

## Alcance propuesto

1. (Primer bloque de trabajo)
2. (Segundo bloque de trabajo)

## Entregables

- (Qué recibe el cliente al terminar)

## Plazos y condiciones

(Calendario estimado, hitos de facturación y condiciones.)

---

{{compania.name}} · {{compania.cif}}
{{compania.address}}, {{compania.city}}
{{compania.email}} · {{compania.phone}}
`

export default function TemplatesSettingsPage() {
  const { t } = useTranslation('settings')
  const auth = useAuth()
  const canWrite =
    auth.status === 'authenticated' &&
    (auth.user.role === 'ADMIN' || auth.user.role === 'CONSULTOR')

  const [items, setItems] = useState<DocumentTemplateDto[]>([])
  const [catalog, setCatalog] = useState<TemplateVariableCatalogItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Estado del editor
  const [name, setName] = useState('')
  const [category, setCategory] = useState<DocumentTemplateCategoryT>('PROPUESTA')
  const [description, setDescription] = useState('')
  const [body, setBody] = useState(STARTER_TEMPLATE)
  const [saving, setSaving] = useState(false)
  const [preview, setPreview] = useState<string>('')
  const [previewLoading, setPreviewLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<DocumentTemplateListResponse>('/document-templates')
      setItems(resp.items)
      setCatalog(resp.variableCatalog)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('templates.errors.load'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void load()
  }, [load])

  const selected = useMemo(
    () => items.find((i) => i.id === selectedId) ?? null,
    [items, selectedId]
  )

  // Cuando seleccionamos una plantilla, rellena el editor.
  useEffect(() => {
    if (selected) {
      setName(selected.name)
      setCategory(selected.category)
      setDescription(selected.description ?? '')
      setBody(selected.body)
    }
  }, [selected])

  // Refresca preview cada vez que cambia el body.
  useEffect(() => {
    const t = window.setTimeout(async () => {
      setPreviewLoading(true)
      try {
        const r = await api.post<{ rendered: string }>(
          `/document-templates/${selectedId ?? 'preview'}/preview`,
          { body }
        )
        setPreview(r.rendered)
      } catch {
        setPreview(body) // fallback: muestra sin sustituir
      } finally {
        setPreviewLoading(false)
      }
    }, 400)
    return () => window.clearTimeout(t)
  }, [body, selectedId])

  const onNew = () => {
    setSelectedId(null)
    setName('')
    setCategory('PROPUESTA')
    setDescription('')
    setBody(STARTER_TEMPLATE)
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !body.trim()) {
      setError(t('templates.errors.nameAndBodyRequired'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const payload = {
        name: name.trim(),
        category,
        description: description.trim() || null,
        body,
      }
      if (selectedId) {
        const r = await api.patch<{ template: DocumentTemplateDto }>(
          `/document-templates/${selectedId}`,
          payload
        )
        setSelectedId(r.template.id)
      } else {
        const r = await api.post<{ template: DocumentTemplateDto }>(
          '/document-templates',
          payload
        )
        setSelectedId(r.template.id)
      }
      await load()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : t('templates.errors.generic')))
    } finally {
      setSaving(false)
    }
  }

  const onDelete = async () => {
    if (!selectedId) return
    if (!(await confirmDialog({ message: t('templates.confirmDelete'), tone: 'danger' }))) return
    try {
      await api.delete(`/document-templates/${selectedId}`)
      setSelectedId(null)
      onNew()
      await load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('templates.errors.generic'))
    }
  }

  const copyVar = async (key: string) => {
    try {
      await navigator.clipboard.writeText(`{{${key}}}`)
    } catch {
      /* ignore */
    }
  }

  const input =
    'w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary focus:outline-none focus:border-toga-500/50'

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-serif font-semibold t-primary">{t('templates.title')}</h2>
          <p className="t-muted text-sm mt-1">
            {t('templates.subtitle')}
          </p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={onNew}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 hover:shadow-glow-toga flex items-center gap-1.5"
          >
            <Plus size={14} /> {t('templates.newButton')}
          </button>
        )}
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      <div className="grid grid-cols-[240px_1fr_240px] gap-4 min-h-[600px]">
        {/* Lista de plantillas */}
        <aside className="surface p-2 overflow-y-auto max-h-[700px]">
          {loading ? (
            <div className="t-faint text-xs flex items-center gap-1.5 py-2">
              <Loader2 size={11} className="animate-spin" /> {t('templates.loading')}
            </div>
          ) : items.length === 0 ? (
            <p className="text-xs t-faint p-2">{t('templates.empty')}</p>
          ) : (
            <ul className="space-y-0.5">
              {items.map((tpl) => {
                const active = tpl.id === selectedId
                return (
                  <li key={tpl.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(tpl.id)}
                      className={[
                        'w-full text-left px-2 py-1.5 rounded text-sm',
                        active
                          ? 'bg-toga-500/15 text-toga-700 dark:text-toga-200 border border-toga-500/30'
                          : 't-secondary hover:bg-hover border border-transparent',
                      ].join(' ')}
                    >
                      <div className="font-medium truncate">{tpl.name}</div>
                      <div className="text-[10px] t-faint">
                        {t('enums:DocumentTemplateCategory.' + tpl.category)}
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </aside>

        {/* Editor */}
        <form onSubmit={onSubmit} className="surface p-4 space-y-3 flex flex-col">
          <div className="flex items-center gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('templates.namePlaceholder')}
              className={`${input} font-semibold`}
              required
              disabled={!canWrite}
            />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as DocumentTemplateCategoryT)}
              className={input}
              disabled={!canWrite}
            >
              {Object.keys(DocumentTemplateCategoryLabel).map((k) => (
                <option key={k} value={k}>
                  {t('enums:DocumentTemplateCategory.' + k)}
                </option>
              ))}
            </select>
          </div>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('templates.descriptionPlaceholder')}
            className={input}
            disabled={!canWrite}
          />

          <div className="grid grid-cols-2 gap-2 flex-1 min-h-[400px]">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className={`${input} font-mono text-xs whitespace-pre`}
              spellCheck={false}
              disabled={!canWrite}
              style={{ minHeight: 400 }}
            />
            <div className="border border-base rounded p-3 overflow-y-auto bg-[var(--bg-input-soft)] text-sm">
              <div className="text-[10px] uppercase tracking-wider t-faint mb-2 flex items-center gap-2">
                {t('templates.previewLabel')}
                {previewLoading && <Loader2 size={10} className="animate-spin" />}
              </div>
              <div className="prose prose-sm prose-invert max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{preview}</ReactMarkdown>
              </div>
            </div>
          </div>

          {canWrite && (
            <div className="flex justify-between items-center pt-2">
              {selectedId ? (
                <button
                  type="button"
                  onClick={() => void onDelete()}
                  className="px-3 py-1.5 rounded-lg text-sm text-rose-700 dark:text-rose-300 border border-rose-500/30 bg-rose-500/10 hover:bg-rose-500/15 flex items-center gap-1.5"
                >
                  <Trash2 size={13} /> {t('templates.delete')}
                </button>
              ) : (
                <span />
              )}
              <button
                type="submit"
                disabled={saving}
                className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {selectedId ? t('templates.saveChanges') : t('templates.createTemplate')}
              </button>
            </div>
          )}
        </form>

        {/* Catálogo de variables */}
        <aside className="surface p-3 overflow-y-auto max-h-[700px]">
          <h3 className="text-sm font-semibold t-primary mb-2 flex items-center gap-2">
            <FileText size={14} className="text-toga-700 dark:text-toga-300" /> {t('templates.variables')}
          </h3>
          <p className="text-[10px] t-faint mb-2">
            {t('templates.variablesHint', { sample: '{{...}}' })}
          </p>
          <ul className="space-y-0.5 text-xs">
            {catalog.map((v) => (
              <li key={v.key}>
                <button
                  type="button"
                  onClick={() => void copyVar(v.key)}
                  className="w-full text-left px-2 py-1 rounded hover:bg-hover flex items-center gap-1.5"
                  title={v.description}
                >
                  <Copy size={10} className="t-faint shrink-0" />
                  <span className="font-mono text-toga-700 dark:text-toga-300 truncate">
                    {`{{${v.key}}}`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </div>
  )
}
